import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { Message, AgentWithIcon, Attachment, ChatGenerationMode } from '../../../types/chat';
import chatService from '../../../services/api/chatService';
import { uploadAttachment } from '../../../services/api/attachmentService';
import { fileUploadService, uploadFile, uploadLargeFile } from '../../../services/api/fileUploadService';
import s3UploadService from '../../../services/api/s3UploadService';
import { logout } from '../../../redux/auth/authSlice';
import { fetchRecentHistory, addNewChatToHistory } from '../../../redux/sidebar/sidebarSlice';
import { Chat as ChatType, TokenUsageInfo } from '../../../types';
import streamingService, { ToolStreamEvent } from '../../../services/api/streamingService';
import { mergeToolEvent } from '../../../utils/toolCalls';
import { tokenManager } from '../../../utils/tokenManager';
import { useToast } from '../../../contexts/ToastContext';
import type { ChatProductFocusPayload } from '../../../utils/chatProductFocus';

const supportsChatStreaming = (modelType?: string): boolean =>
  modelType?.startsWith('gpt-5') === true || ['gpt-6-astra', 'gpt-6-sol', 'gpt-6-luna'].includes(modelType ?? '');

/**
 * チャットページのアクション処理を管理するカスタムフック
 */
export const useChatActions = (
  chatState: {
    isNewChat: boolean;
    chatId: string | undefined;
    selectedAgent: AgentWithIcon | null;
    messages: Message[];
    setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void;
    messageInput: string;
    setMessageInput: (value: string) => void;
    addMessage: (message: Message) => void;
    setIsLoading: (value: boolean) => void;
    setTypingIndicator: (value: boolean) => void;
    chats: ChatType[];
    setChats: (chats: ChatType[] | ((prev: ChatType[]) => ChatType[])) => void;
    agents: AgentWithIcon[];
    orgTokenInfo: TokenUsageInfo | null;
    setSuggestions?: (suggestions: string[]) => void;
    // ThinkingProcess関連
    showThinkingProcess: boolean;
    setShowThinkingProcess: (value: boolean) => void;
    setThinkingMessage: (value: string) => void;
    setCurrentChatId?: (id: number | null) => void;
    // GPT-5 AIのthinking（reasoning）内容
    aiThinkingContent?: string;
    setAiThinkingContent?: (value: string) => void;
  },
  user: any,
) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  /**
   * ストリーミング完了後にサジェストを非同期ポーリングで取得
   * バックエンドのGenerateSuggestionsJobが完了するまでリトライする
   */
  const pollForSuggestions = (messageId: number, retries = 0) => {
    if (retries >= 3) return;
    setTimeout(async () => {
      try {
        const status = await chatService.getMessageStatus(messageId);
        if (status.suggestions && Array.isArray(status.suggestions) && status.suggestions.length > 0) {
          console.log('💡 サジェスト取得成功:', status.suggestions.length, '個');
          if (chatState.setSuggestions && typeof chatState.setSuggestions === 'function') {
            chatState.setSuggestions(status.suggestions);
          }
        } else {
          pollForSuggestions(messageId, retries + 1);
        }
      } catch (e) {
        console.error('サジェスト取得失敗:', e);
      }
    }, 3000);
  };

  /**
   * 新規チャットを作成しメッセージを送信する処理
   */
  const handleCreateNewChat = async (
    messageContent: string,
    agentToUse: AgentWithIcon, // 使用するエージェントを引数で受け取る
    attachments?: File[],
    uploadedFiles?: any[],
    enableSearch: boolean = false,
    productId?: number,
    generationMode?: ChatGenerationMode,
    productFocus?: ChatProductFocusPayload
  ) => {
    console.log('🎯 useChatActions handleCreateNewChat called:', {
      messageContent,
      agentToUse_id: agentToUse?.id,
      attachments_count: attachments?.length || 0,
      uploadedFiles_count: uploadedFiles?.length || 0,
      uploadedFiles_data: uploadedFiles
    });
    
    if (!agentToUse) {
      showToast('送信するAIエージェントが指定されていません。', 'error');
      chatState.setIsLoading(false); // ローディング解除
      return;
    }
    
    // chatState.setIsLoading(true); // 呼び出し元で設定済み

    try {
      // チャット作成APIを呼び出し（初期メッセージは含めない）
      const createChatData = {
        agent_id: agentToUse.id
      };
      
      console.log('handleCreateNewChat: チャット作成リクエスト', createChatData);
      const createChatResponse = await chatService.createChat(createChatData);
      
      // 新しいチャットIDを取得
      const newChatId = createChatResponse.chat.id;
      // ルート更新前でもSSE接続できるよう、現在のチャットIDを保持
      if (chatState.setCurrentChatId) {
        chatState.setCurrentChatId(newChatId);
      }

      // チャット作成直後にサイドバーの履歴を更新
      // 入力メッセージの先頭部分をタイトルとして表示
      const tempTitle = messageContent.length > 30
        ? messageContent.substring(0, 30) + '...'
        : messageContent;
      dispatch(addNewChatToHistory({
        id: newChatId,
        title: tempTitle,
        agent_name: agentToUse.title,
        agent_color: agentToUse.base_color,
        agent_model_type: agentToUse.model_type
      }));

      // UIにユーザーメッセージを追加（すぐに表示）
      const userMessage: Message = {
        id: Date.now(),
        content: messageContent,
        sender_type: 'user',
        created_at: new Date().toISOString(),
        uploaded_files: uploadedFiles || undefined
      };
      
      // 添付ファイルがある場合、処理を分岐
      if (attachments && attachments.length > 0) {
        try {
          console.log('新規チャット: 添付ファイルを処理します:', attachments.length, '個');
          
          // ドキュメントファイルをチェック
          const documentFiles = attachments.filter(file => 
            fileUploadService.isSupportedDocumentFile(file)
          );
          
          // サポートされていないファイルをチェック（すべてのメディアファイルはdocumentFilesに含まれる）
          const mediaFiles = attachments.filter(file => 
            !fileUploadService.isSupportedDocumentFile(file)
          );
          
          console.log('📁 ファイル分類結果:', {
            total_files: attachments.length,
            document_files: documentFiles.map(f => ({ name: f.name, type: f.type })),
            media_files: mediaFiles.map(f => ({ name: f.name, type: f.type })),
            is_image: attachments.map(f => ({ name: f.name, isImage: fileUploadService.isImageFile(f) }))
          });
          
          // ドキュメントファイルがある場合（Excel、PowerPoint、画像、動画、PDF等）
          if (documentFiles.length > 0) {
            console.log('新規チャット: ドキュメントファイルをアップロードします:', documentFiles.length, '個');
            
            const uploadedAttachments: Attachment[] = [];
            for (const file of documentFiles) {
              console.log('新規チャット: ファイルをアップロード中...', file.name);
              const result = await uploadAttachment(file, newChatId, userMessage.id);
              uploadedAttachments.push(result);
            }
            
            // 既存の添付ファイルに追加
            if (userMessage.attachments) {
              userMessage.attachments.push(...uploadedAttachments);
            } else {
              userMessage.attachments = uploadedAttachments;
            }
            
            console.log('新規チャット: ドキュメントファイルのアップロード完了');
          }
          
          // サポートされていないファイルがある場合（通常ここには来ない）
          if (mediaFiles.length > 0) {
            console.log('新規チャット: サポートされていないファイルがあります:', mediaFiles.map(f => f.name));
            // サポートされていないファイルはスキップするか、エラーを表示
            showToast('サポートされていないファイル形式が含まれています。', 'error');
          }
        } catch (error) {
          console.error('新規チャット: ファイル処理に失敗しました:', error);
          showToast('ファイルの処理に失敗しました。メッセージのみ送信します。', 'error');
          // エラーでもメッセージは送信続行
        }
      }
      
      // ユーザーメッセージを追加
      chatState.addMessage(userMessage);
      
      // チャット作成後、メッセージを送信（タイムアウト対策のため分離）
      // 生成モードは商品だけでも生成できるため、本文が空でも送る。
      if (messageContent || (generationMode && productId)) {
        console.log('新規チャット: チャット作成完了、メッセージを別途送信');
        
        // ThinkingProcessを表示（機能フラグで制御）
        if (chatState.setShowThinkingProcess && chatState.setThinkingMessage) {
          chatState.setShowThinkingProcess(true);
          chatState.setThinkingMessage(messageContent);
        }
        
        try {
          // 対応モデルの場合はストリーミングを有効にする（生成モードは結果をSSEのカードで返すため常にストリーミング）
          const useStreaming = Boolean(generationMode) || supportsChatStreaming(agentToUse?.model_type);

          // 非同期メッセージを送信（チャット作成とは別のAPIコール）
          const response = await chatService.sendMessage({
            chat_id: newChatId,
            content: messageContent,
            has_attachments: attachments && attachments.length > 0,
            uploaded_files: userMessage.uploaded_files,
            enable_search: enableSearch,
            product_id: productId,
            enable_streaming: useStreaming,
            generation_mode: generationMode,
            product_focus: productFocus
          });
          
          console.log('新規チャット: 非同期メッセージ送信成功', response);
        
          // AIメッセージのプレースホルダーを追加
          const aiMessagePlaceholder = {
            id: response.ai_message_id,
            content: '',
            sender_type: 'ai' as const,
            created_at: new Date().toISOString(),
            status: 'processing' as const
          };
          chatState.addMessage(aiMessagePlaceholder);

          // 対応モデルの場合はストリーミング、それ以外はポーリング
          console.log('🔍 [新規チャット] Agent model check:', {
            agentId: agentToUse?.id,
            agentTitle: agentToUse?.title,
            modelType: agentToUse?.model_type,
            hasModelType: !!agentToUse?.model_type
          });
          console.log('🔍 [新規チャット] useStreaming:', useStreaming);

          if (useStreaming) {
            // AIストリーミング接続を開始
            console.log('🔄 [新規チャット] Starting AI streaming for message:', response.ai_message_id);

            // トークンを取得（tokenManager から）
            const token = tokenManager.getToken() || '';
            console.log('🔑 [新規チャット] Token for streaming:', {
              hasToken: !!token,
              tokenLength: token?.length || 0,
              tokenPreview: token ? token.substring(0, 10) + '...' : 'NO TOKEN'
            });

            streamingService.startStreaming({
              chatId: newChatId,
              messageId: response.ai_message_id,
              token,
              enableSearch,
              productId,
              onDelta: (delta: string, fullResponse: string) => {
                // デバッグログ - 受信したデータを即座に表示
                console.log('📝 [新規チャット] onDelta received:', {
                  delta_length: delta?.length || 0,
                  delta_preview: delta?.substring(0, 50),
                  fullResponse_length: fullResponse?.length || 0,
                  fullResponse_preview: fullResponse?.substring(0, 100)
                });

                // リアルタイムでメッセージを更新
                chatState.setMessages((prevMessages: Message[]) => {
                  return prevMessages.map((msg) =>
                    msg.id === response.ai_message_id
                      ? { ...msg, content: fullResponse, status: 'processing' as const }
                      : msg
                  );
                });
              },
              onComplete: (messageId: number, content: string) => {
                console.log('✅ [新規チャット] Streaming completed:', messageId);

                // AI応答完了
                chatState.setTypingIndicator(false);
                chatState.setShowThinkingProcess(false);
                chatState.setThinkingMessage('');
                // AIのthinking内容をクリア
                if (chatState.setAiThinkingContent) {
                  chatState.setAiThinkingContent('');
                }

                // メッセージを最終更新
                const aiMessage: Message = {
                  id: messageId,
                  content,
                  sender_type: 'ai',
                  created_at: new Date().toISOString(),
                  status: 'completed'
                };
                chatState.setMessages((prevMessages: Message[]) => {
                  return prevMessages.map((msg) =>
                    msg.id === messageId ? { ...msg, ...aiMessage } : msg
                  );
                });

                // チャット情報を取得して更新
                chatService.getChatById(newChatId).then((chatResponse) => {
                  if (chatResponse) {
                    console.log('[新規チャット] チャット情報を取得:', chatResponse.title);
                    chatState.setChats(prevChats => [...prevChats, chatResponse]);
                  }
                }).catch((error) => {
                  console.error('チャット情報の取得に失敗:', error);
                }).finally(async () => {
                  // ロード状態解除
                  chatState.setIsLoading(false);

                  // ナビゲーション（先に実行）
                  navigate(`/chat/${newChatId}`);

                  // タイトル生成ジョブ（GenerateChatTitleJob）の完了を待つため遅延
                  // バックエンドでAI応答完了後にタイトル生成が非同期実行されるため
                  setTimeout(() => {
                    dispatch(fetchRecentHistory());
                  }, 2000);
                });

                // サジェストを非同期ポーリングで取得
                pollForSuggestions(messageId);
              },
              onError: (error: string) => {
                console.error('❌ [新規チャット] Streaming error:', error);

                // エラー時は通常のポーリングにフォールバック
                chatState.setShowThinkingProcess(false);
                chatState.setThinkingMessage('');
                chatState.setTypingIndicator(false);
                chatState.setIsLoading(false);

                showToast(`ストリーミング接続に失敗しました: ${error}`, 'error');
              },
              onSearchProgress: (message: string) => {
                console.log('🔍 [新規チャット] Search progress:', message);
              },
              onSearchResult: (result) => {
                console.log('📄 [新規チャット] Search result:', result);
              },
              onSearchError: (error: string) => {
                console.error('❌ [新規チャット] Search error:', error);
              },
              onThinking: (delta: string, fullThinking: string) => {
                console.log('💭 [新規チャット] onThinking received:', {
                  delta_length: delta?.length || 0,
                  fullThinking_length: fullThinking?.length || 0,
                  delta_preview: delta?.substring(0, 50)
                });
                if (chatState.setAiThinkingContent) {
                  chatState.setAiThinkingContent(fullThinking);
                }
              },
              onToolEvent: (event: ToolStreamEvent) => {
                chatState.setMessages((prevMessages: Message[]) => {
                  return prevMessages.map((msg) =>
                    msg.id === response.ai_message_id
                      ? { ...msg, tool_calls: mergeToolEvent(msg.tool_calls, event) }
                      : msg
                  );
                });
              }
            });

            return; // ストリーミングを使用する場合、ポーリングは実行しない
          }

          // ポーリングでAI応答を待つ（ストリーミング非対応モデル用）
          const pollForResponse = async () => {
            try {
              const status = await chatService.getMessageStatus(response.ai_message_id);
              
              if (status.status === 'completed') {
                console.log('新規チャット: AI応答が完了:', status.content?.substring(0, 30) + '...');
            
                // AI応答をUI更新
                const aiResponse: Message = {
                  id: response.ai_message_id,
                  content: status.content || '',
                  sender_type: 'ai',
                  created_at: status.created_at,
                  suggestions: status.suggestions,
                  status: 'completed'
                };
            
                // サジェストを確認して設定
                if (status.suggestions && Array.isArray(status.suggestions)) {
                  console.log('新規チャット: AIから返答サジェストを受信:', status.suggestions.length, '個');
                  
                  if (chatState.setSuggestions && typeof chatState.setSuggestions === 'function') {
                    chatState.setSuggestions(status.suggestions);
                  }
                }
            
                // ThinkingProcessを閉じてからメッセージを更新
                if (chatState.showThinkingProcess) {
                  chatState.setShowThinkingProcess(false);
                  chatState.setThinkingMessage('');
                }
                
                // プレースホルダーを実際のメッセージに置き換え（暫定的にaddMessageで対応）
                chatState.addMessage(aiResponse);
            
                // チャット情報を取得して更新
                try {
                  const chatResponse = await chatService.getChatById(newChatId);
                  if (chatResponse) {
                    console.log('新規チャット: チャット情報を取得:', chatResponse.title);
                    chatState.setChats(prevChats => [...prevChats, chatResponse]);
                  }
                } catch (error) {
                  console.error('チャット情報の取得に失敗:', error);
                } finally {
                  // サイドバーの履歴を再取得してタイトルを更新（成功・失敗問わず）
                  dispatch(fetchRecentHistory());
                }

                // ロード状態とタイピングインジケーターを解除
                chatState.setIsLoading(false);
                chatState.setTypingIndicator(false);

                // 少し遅延してからナビゲーション
                setTimeout(() => {
                  navigate(`/chat/${newChatId}`);
                }, 500);
              } else if (status.status === 'failed') {
                showToast('AI応答の処理に失敗しました。', 'error');
                chatState.setIsLoading(false);
                chatState.setTypingIndicator(false);
                if (chatState.showThinkingProcess) {
                  chatState.setShowThinkingProcess(false);
                  chatState.setThinkingMessage('');
                }
              } else {
                // まだ処理中の場合、2秒後に再確認
                setTimeout(pollForResponse, 2000);
              }
            } catch (error) {
              console.error('新規チャット: AI応答の取得に失敗:', error);
              showToast('AI応答の取得に失敗しました。', 'error');
              chatState.setIsLoading(false);
              chatState.setTypingIndicator(false);
              if (chatState.showThinkingProcess) {
                chatState.setShowThinkingProcess(false);
                chatState.setThinkingMessage('');
              }
            }
          };
          
          // 2秒後にポーリング開始
          setTimeout(pollForResponse, 2000);
        } catch (error) {
          console.error('メッセージ送信エラー:', error);
          showToast('メッセージの送信に失敗しました。', 'error');
          
          // エラー時にローディング状態を解除
          chatState.setIsLoading(false);
          chatState.setTypingIndicator(false);
          
          // ThinkingProcessを閉じる
          if (chatState.showThinkingProcess) {
            chatState.setShowThinkingProcess(false);
            chatState.setThinkingMessage('');
          }
        }
      } else {
        // 初期メッセージがない場合（通常このケースは発生しない）
        console.log('新規チャット: 初期メッセージがないため、ページ遷移のみを実行します');
        chatState.setIsLoading(false);
        chatState.setTypingIndicator(false);
        setTimeout(() => {
          navigate(`/chat/${newChatId}`);
        }, 300);
      }
    } catch (error) {
      console.error('チャット作成エラー:', error);
      showToast('チャットの作成に失敗しました。もう一度お試しください。', 'error');
      
      // エラー時に確実にローディング状態を解除
      chatState.setIsLoading(false);
      chatState.setTypingIndicator(false);
      
      // ThinkingProcessを閉じる
      if (chatState.showThinkingProcess) {
        chatState.setShowThinkingProcess(false);
        chatState.setThinkingMessage('');
      }
    }
  };
  
  /**
   * 指定されたメッセージ内容を直接送信する処理（サジェスト用）
   */
  const handleSendDirectMessage = async (messageContent: string, attachments?: File[], uploadedFiles?: any[], enableSearch: boolean = false, productId?: number) => {
    // メッセージまたは添付ファイルがない場合は送信しない
    if (!messageContent.trim() && (!attachments || attachments.length === 0)) return;

    // クレジット制限チェック
    if (user) {
      // 組織に所属している場合は組織のクレジット制限を確認
      if (user.organization_id && chatState.orgTokenInfo) {
        if (chatState.orgTokenInfo.is_limit_reached) {
          showToast('組織のクレジット上限に達しました。管理者にお問い合わせください。', 'error');
          return;
        }
      }
    }

    chatState.setIsLoading(true);
    
    // 入力欄をクリア
    chatState.setMessageInput('');
    
    try {
      if (chatState.isNewChat) {
        let agentToUse = chatState.selectedAgent;
        if (!agentToUse) {
          const defaultAgent = chatState.agents.find(agent => agent.id === 1);
          if (defaultAgent) {
            agentToUse = defaultAgent;
          } else {
            showToast('デフォルトのエージェント(ID:1)が見つかりませんでした。リストからエージェントを選択してください。', 'error');
            chatState.setIsLoading(false);
            return;
          }
        }
        await handleCreateNewChat(messageContent, agentToUse, attachments, uploadedFiles, enableSearch, productId);
        return;
      }
      
      // 既存のチャットIDを数値変換
      if (!chatState.chatId || isNaN(Number(chatState.chatId))) {
        let agentToUseForFallback = chatState.selectedAgent;
        if(!agentToUseForFallback) {
            const defaultAgent = chatState.agents.find(agent => agent.id === 1);
            if(defaultAgent) agentToUseForFallback = defaultAgent;
            else {
                showToast('送信先エージェントが見つかりません。', 'error');
                chatState.setIsLoading(false);
                return;
            }
        }
        await handleCreateNewChat(messageContent, agentToUseForFallback, attachments, uploadedFiles, enableSearch, productId);
        return;
      }
      
      const currentChatId = Number(chatState.chatId);
      
      // ユーザーメッセージをUIに追加（すぐに表示）
      const userMessage: Message = {
        id: chatState.messages.length + 1,
        content: messageContent,
        sender_type: 'user',
        created_at: new Date().toISOString(),
        uploaded_files: uploadedFiles || undefined
      };
      
      // 添付ファイルがある場合、処理を分岐
      if (attachments && attachments.length > 0) {
        try {
          console.log('添付ファイルを処理します:', attachments.length, '個');
          
          // ドキュメントファイルをチェック
          const documentFiles = attachments.filter(file => 
            fileUploadService.isSupportedDocumentFile(file)
          );
          
          // サポートされていないファイルをチェック（すべてのメディアファイルはdocumentFilesに含まれる）
          const mediaFiles = attachments.filter(file => 
            !fileUploadService.isSupportedDocumentFile(file)
          );
          
          console.log('📁 ファイル分類結果:', {
            total_files: attachments.length,
            document_files: documentFiles.map(f => ({ name: f.name, type: f.type })),
            media_files: mediaFiles.map(f => ({ name: f.name, type: f.type })),
            is_image: attachments.map(f => ({ name: f.name, isImage: fileUploadService.isImageFile(f) }))
          });
          
          // ドキュメントファイルがある場合（Excel、PowerPoint、画像、動画、PDF等）
          if (documentFiles.length > 0) {
            console.log('ドキュメントファイルをアップロードします:', documentFiles.length, '個');
            
            const uploadedAttachments: Attachment[] = [];
            for (const file of documentFiles) {
              console.log('ファイルをアップロード中...', file.name);
              const result = await uploadAttachment(file, currentChatId, userMessage.id);
              uploadedAttachments.push(result);
            }
            
            // 既存の添付ファイルに追加
            if (userMessage.attachments) {
              userMessage.attachments.push(...uploadedAttachments);
            } else {
              userMessage.attachments = uploadedAttachments;
            }
            
            console.log('ドキュメントファイルのアップロード完了');
          }
          
          // サポートされていないファイルがある場合（通常ここには来ない）
          if (mediaFiles.length > 0) {
            console.log('サポートされていないファイルがあります:', mediaFiles.map(f => f.name));
            // サポートされていないファイルはスキップするか、エラーを表示
            showToast('サポートされていないファイル形式が含まれています。', 'error');
          }
        } catch (error) {
          console.error('ファイル処理に失敗しました:', error);
          showToast('ファイルの処理に失敗しました。メッセージのみ送信します。', 'error');
        }
      }
      
      // 応答待ちタイピングインジケーターを表示する
      console.log('handleSendDirectMessage: 送信処理開始', {
        originalMessage: messageContent.substring(0, 50),
        hasAttachments: attachments?.length || 0,
        convertedLength: messageContent.length
      });
      chatState.setTypingIndicator(true);
      
      // ThinkingProcessを表示（機能フラグで制御）
      if (chatState.setShowThinkingProcess && chatState.setThinkingMessage) {
        chatState.setShowThinkingProcess(true);
        chatState.setThinkingMessage(messageContent);
      }
      
      // ユーザーメッセージはアニメーションなしで即時追加
      chatState.addMessage(userMessage);

      // メッセージ送信直後にサイドバーの履歴を更新（履歴の順番更新）
      dispatch(fetchRecentHistory());

      // 対応モデルの場合はストリーミングを有効にする
      const useStreaming = supportsChatStreaming(chatState.selectedAgent?.model_type);

      // 非同期APIでメッセージを送信（添付ファイルとアップロード済みファイル情報を含める）
      const response = await chatService.sendMessage({
        chat_id: currentChatId,
        content: messageContent,
        has_attachments: attachments && attachments.length > 0,
        uploaded_files: userMessage.uploaded_files,
        enable_search: enableSearch,
        product_id: productId,
        enable_streaming: useStreaming
      });

      // AIメッセージのプレースホルダーを追加
      const aiMessagePlaceholder: Message = {
        id: response.ai_message_id,
        content: '',
        sender_type: 'ai',
        created_at: new Date().toISOString(),
        status: 'processing'
      };
      chatState.addMessage(aiMessagePlaceholder);

      // 対応モデルの場合はストリーミング、それ以外はポーリング
      if (useStreaming) {
        console.log('🔄 [handleSendDirectMessage] Starting AI streaming for message:', response.ai_message_id);
        const token = tokenManager.getToken() || '';
        streamingService.startStreaming({
          chatId: currentChatId,
          messageId: response.ai_message_id,
          token,
          enableSearch,
          productId,
          onDelta: (delta: string, fullResponse: string) => {
            // デバッグログ - 受信したデータを即座に表示
            console.log('📝 [handleSendDirectMessage] onDelta received:', {
              delta_length: delta?.length || 0,
              delta_preview: delta?.substring(0, 50),
              fullResponse_length: fullResponse?.length || 0,
              fullResponse_preview: fullResponse?.substring(0, 100)
            });

            chatState.setMessages((prevMessages: Message[]) => {
              return prevMessages.map((msg) =>
                msg.id === response.ai_message_id
                  ? { ...msg, content: fullResponse, status: 'processing' as const }
                  : msg
              );
            });
          },
          onComplete: (messageId: number, content: string) => {
            console.log('✅ [handleSendDirectMessage] Streaming completed:', messageId);
            chatState.setTypingIndicator(false);
            if (chatState.showThinkingProcess) {
              chatState.setShowThinkingProcess(false);
              chatState.setThinkingMessage('');
            }
            // AIのthinking内容をクリア
            if (chatState.setAiThinkingContent) {
              chatState.setAiThinkingContent('');
            }
            const aiMessage: Message = {
              id: messageId,
              content,
              sender_type: 'ai',
              created_at: new Date().toISOString(),
              status: 'completed'
            };
            chatState.setMessages((prevMessages: Message[]) => {
              return prevMessages.map((msg) =>
                msg.id === messageId ? { ...msg, ...aiMessage } : msg
              );
            });
            chatState.setIsLoading(false);

            // サジェストを非同期ポーリングで取得
            pollForSuggestions(messageId);
          },
          onError: (error: string) => {
            console.error('❌ [handleSendDirectMessage] Streaming error:', error);
            if (chatState.showThinkingProcess) {
              chatState.setShowThinkingProcess(false);
              chatState.setThinkingMessage('');
            }
            // AIのthinking内容をクリア
            if (chatState.setAiThinkingContent) {
              chatState.setAiThinkingContent('');
            }
            chatState.setTypingIndicator(false);
            chatState.setIsLoading(false);
          },
          onSearchProgress: (message: string) => console.log('🔍 Search progress:', message),
          onSearchResult: (result) => console.log('📄 Search result:', result),
          onSearchError: (error: string) => console.error('❌ Search error:', error),
          onThinking: (delta: string, fullThinking: string) => {
            console.log('💭 [handleSendDirectMessage] onThinking received:', {
              delta_length: delta?.length || 0,
              fullThinking_length: fullThinking?.length || 0
            });
            if (chatState.setAiThinkingContent) {
              chatState.setAiThinkingContent(fullThinking);
            }
          },
          onToolEvent: (event: ToolStreamEvent) => {
            chatState.setMessages((prevMessages: Message[]) => {
              return prevMessages.map((msg) =>
                msg.id === response.ai_message_id
                  ? { ...msg, tool_calls: mergeToolEvent(msg.tool_calls, event) }
                  : msg
              );
            });
          }
        });
        return; // ストリーミングを使用する場合、ポーリングは実行しない
      }

      // ポーリングでAI応答を待つ（ストリーミング非対応モデル用）
      const pollForResponse = async () => {
        try {
          const status = await chatService.getMessageStatus(response.ai_message_id);

          if (status.status === 'completed') {
            console.log('新しいAI応答を受信:', status.content?.substring(0, 30) + '...');

            const aiResponse: Message = {
              id: response.ai_message_id,
              content: status.content || '',
              sender_type: 'ai',
              created_at: status.created_at,
              suggestions: status.suggestions,
              status: 'completed'
            };

            // サジェストを確認
            if (status.suggestions && Array.isArray(status.suggestions)) {
              console.log('AIから返答サジェストを受信:', status.suggestions.length, '個');

              if (chatState.setSuggestions && typeof chatState.setSuggestions === 'function') {
                chatState.setSuggestions(status.suggestions);
              }
            }

            // ThinkingProcessを閉じてからメッセージを更新
            if (chatState.showThinkingProcess) {
              chatState.setShowThinkingProcess(false);
              chatState.setThinkingMessage('');
            }

            // プレースホルダーを実際のメッセージに置き換え（暫定的にaddMessageで対応）
            chatState.addMessage(aiResponse);

            // チャット情報を更新
            try {
              const chatResponse = await chatService.getChatById(currentChatId);
              if (chatResponse) {
                chatState.setChats(prevChats =>
                  prevChats.map(chat =>
                    chat.id === chatResponse.id
                      ? { ...chat, title: chatResponse.title || chat.title }
                      : chat
                  )
                );
              }
            } catch (error) {
              console.error('チャット情報の取得に失敗:', error);
            } finally {
              // サイドバーの履歴を再取得してタイトルを更新（成功・失敗問わず）
              dispatch(fetchRecentHistory());
            }

            // ロード状態とタイピングインジケーターを解除
            chatState.setTypingIndicator(false);
            chatState.setIsLoading(false);
          } else if (status.status === 'failed') {
            showToast('AI応答の処理に失敗しました。', 'error');
            chatState.setTypingIndicator(false);
            chatState.setIsLoading(false);
            if (chatState.showThinkingProcess) {
              chatState.setShowThinkingProcess(false);
              chatState.setThinkingMessage('');
            }
          } else {
            // まだ処理中の場合、2秒後に再確認
            setTimeout(pollForResponse, 2000);
          }
        } catch (error) {
          console.error('AI応答の取得に失敗:', error);
          showToast('AI応答の取得に失敗しました。', 'error');
          chatState.setTypingIndicator(false);
          chatState.setIsLoading(false);
          if (chatState.showThinkingProcess) {
            chatState.setShowThinkingProcess(false);
            chatState.setThinkingMessage('');
          }
        }
      };

      // 2秒後にポーリング開始
      setTimeout(pollForResponse, 2000);
    } catch (error) {
      showToast('メッセージの送信に失敗しました。もう一度お試しください。', 'error');
      chatState.setTypingIndicator(false);
      chatState.setIsLoading(false);
    }
  };

  /**
   * メッセージを非同期で送信する処理（添付ファイル付き）
   */
  const handleSendMessageAsync = async (
    attachments?: File[],
    uploadedFiles?: any[],
    enableSearchOrCallback?: boolean | ((messageId: number) => void),
    productId?: number,
    onPollingUpdate?: (status: any) => void,
    onPollingComplete?: (assistantMessage: any) => void,
    onPollingError?: (error: string) => void,
    generationMode?: ChatGenerationMode,
    overrideText?: string,
    productFocus?: ChatProductFocusPayload
  ) => {
    // パラメータを正しく処理
    let enableSearch = false;

    if (typeof enableSearchOrCallback === 'boolean') {
      enableSearch = enableSearchOrCallback;
    }
    
    console.log('🎯 useChatActions handleSendMessageAsync called:', {
      messageInput: chatState.messageInput,
      attachments_count: attachments?.length || 0,
      uploadedFiles_count: uploadedFiles?.length || 0,
      uploadedFiles_data: uploadedFiles,
      selectedAgent: chatState.selectedAgent?.id,
      chatId: chatState.chatId,
      isNewChat: chatState.isNewChat,
      enableSearchOrCallback,
      enableSearch,
      productId,
      generationMode
    });

    const inputText = overrideText ?? chatState.messageInput;
    // メッセージまたは添付ファイル、アップロード済みファイル、商品選択がない場合は送信しない
    if (!inputText.trim() && (!attachments || attachments.length === 0) && (!uploadedFiles || uploadedFiles.length === 0) && !productId) return;

    // クレジット制限チェック
    if (user) {
      // 組織に所属している場合は組織のクレジット制限を確認
      if (user.organization_id && chatState.orgTokenInfo) {
        if (chatState.orgTokenInfo.is_limit_reached) {
          showToast('組織のクレジット上限に達しました。管理者にお問い合わせください。', 'error');
          return;
        }
      }
    }

    chatState.setIsLoading(true);
    
    // 送信するメッセージの内容を保存
    let messageContent = inputText;
    // 入力欄をクリア（カードのボタン経由 overrideText のときは書きかけの下書きを残す）
    if (overrideText === undefined) {
      chatState.setMessageInput('');
    }

    try {
      // 既存のチャットIDを数値変換
      if (!chatState.chatId || isNaN(Number(chatState.chatId))) {
        // 既存チャットIDが無効な場合は、新規チャットとして扱う（フォールバック）
        let agentToUseForFallback = chatState.selectedAgent;
        if(!agentToUseForFallback) {
            const defaultAgent = chatState.agents.find(agent => agent.id === 1);
            if(defaultAgent) agentToUseForFallback = defaultAgent;
            else {
                showToast('送信先エージェントが見つかりません。', 'error');
                chatState.setIsLoading(false);
                return;
            }
        }
        await handleCreateNewChat(messageContent, agentToUseForFallback, attachments, uploadedFiles, enableSearch, productId, generationMode, productFocus);
        return;
      }

      const currentChatId = Number(chatState.chatId);
      // 念のため現在のチャットIDを状態に反映
      if (chatState.setCurrentChatId) {
        chatState.setCurrentChatId(currentChatId);
      }
      
      // ユーザーメッセージをUIに追加（すぐに表示）
      const userMessage: Message = {
        id: chatState.messages.length + 1,
        content: messageContent,
        sender_type: 'user',
        created_at: new Date().toISOString(),
        uploaded_files: uploadedFiles || undefined
      };
      
      // 添付ファイルがある場合、処理を分岐
      if (attachments && attachments.length > 0) {
        try {
          console.log('handleSendMessageAsync: 添付ファイルを処理します:', attachments.length, '個');
          
          // ドキュメントファイルをチェック
          const documentFiles = attachments.filter(file => 
            fileUploadService.isSupportedDocumentFile(file)
          );
          
          // サポートされていないファイルをチェック（すべてのメディアファイルはdocumentFilesに含まれる）
          const mediaFiles = attachments.filter(file => 
            !fileUploadService.isSupportedDocumentFile(file)
          );
          
          console.log('📁 ファイル分類結果:', {
            total_files: attachments.length,
            document_files: documentFiles.map(f => ({ name: f.name, type: f.type })),
            media_files: mediaFiles.map(f => ({ name: f.name, type: f.type })),
            is_image: attachments.map(f => ({ name: f.name, isImage: fileUploadService.isImageFile(f) }))
          });
          
          // ドキュメントファイルがある場合はAIサービスに直接アップロード（Excel、PowerPoint、画像、動画、PDF等）
          if (documentFiles.length > 0) {
            console.log('handleSendMessageAsync: ドキュメントファイルをAIサービスにアップロードします:', documentFiles.length, '個');
            
            for (const file of documentFiles) {
              const isExcel = fileUploadService.isExcelFile(file);
              const isPowerPoint = fileUploadService.isPowerPointFile(file);
              const isImage = fileUploadService.isImageFile(file);
              const isVideo = fileUploadService.isVideoFile(file);
              const isPDF = fileUploadService.isPDFFile(file);
              const isDocument = fileUploadService.isDocumentFile(file);
              
              // すべてのサポートされているファイルをFileAPIで処理
              if (isExcel || isPowerPoint || isImage || isVideo || isPDF || isDocument) {
                // Excel/PowerPoint/画像はAIサービスに直接アップロード
                let aiModel = chatState.selectedAgent?.model_type || 'openai';
                if (aiModel.startsWith('o1') || aiModel.startsWith('o3') || aiModel.startsWith('gpt')) {
                  aiModel = 'openai';
                } else if (aiModel.startsWith('claude')) {
                  aiModel = 'claude';
                } else if (aiModel.startsWith('gemini')) {
                  aiModel = 'gemini';
                }
                
                try {
                  let uploadedFile;
                  if (file.size > 10 * 1024 * 1024) {
                    uploadedFile = await uploadLargeFile(file, aiModel, currentChatId, (progress) => {
                      console.log('アップロード進捗:', progress, '%');
                    });
                  } else {
                    uploadedFile = await uploadFile(file, aiModel, currentChatId);
                  }
                  
                  // 画像ファイルの場合、S3にもアップロード
                  if (isImage) {
                    console.log('🖼️ 画像をS3にもアップロード:', file.name);
                    const s3Result = await s3UploadService.uploadImageToS3(file);
                    if (s3Result) {
                      console.log('✅ S3アップロード成功:', s3Result.s3_url);
                      // S3のURLを保存（将来的に使用）
                      if (uploadedFile) {
                        uploadedFile.s3_url = s3Result.s3_url;
                        uploadedFile.s3_key = s3Result.s3_key;
                      }
                    }
                  }
                  
                  if (uploadedFile && uploadedFile.file_id) {
                    messageContent += `\n\n[アップロード済みファイル: ${file.name} (ID: ${uploadedFile.file_id})]`;
                    userMessage.content = messageContent;
                    
                    if (!userMessage.uploaded_files) {
                      userMessage.uploaded_files = [];
                    }
                    userMessage.uploaded_files.push({
                      file_id: uploadedFile.file_id,
                      service: aiModel,
                      name: file.name,
                      mime_type: file.type,
                      s3_url: uploadedFile.s3_url,
                      s3_key: uploadedFile.s3_key,
                      s3_bucket: uploadedFile.s3_bucket
                    });
                  }
                } catch (error) {
                  console.error('💥 AIサービスへのファイルアップロードに失敗:', error);
                  const result = await uploadAttachment(file, currentChatId, userMessage.id);
                  if (userMessage.attachments) {
                    userMessage.attachments.push(result);
                  } else {
                    userMessage.attachments = [result];
                  }
                }
              } else {
                const result = await uploadAttachment(file, currentChatId, userMessage.id);
                if (userMessage.attachments) {
                  userMessage.attachments.push(result);
                } else {
                  userMessage.attachments = [result];
                }
              }
            }
          }
          
          // サポートされていないファイルがある場合（通常ここには来ない）
          if (mediaFiles.length > 0) {
            console.log('handleSendMessageAsync: サポートされていないファイルがあります:', mediaFiles.map(f => f.name));
            // サポートされていないファイルはスキップするか、エラーを表示
            showToast('サポートされていないファイル形式が含まれています。', 'error');
          }
        } catch (error) {
          console.error('handleSendMessageAsync: ファイル処理に失敗しました:', error);
          showToast('ファイルの処理に失敗しました。メッセージのみ送信します。', 'error');
        }
      }
      
      // 応答待ちタイピングインジケーターを表示する
      chatState.setTypingIndicator(true);

      // ユーザーメッセージはアニメーションなしで即時追加
      chatState.addMessage(userMessage);

      // メッセージ送信直後にサイドバーの履歴を更新（履歴の順番更新）
      dispatch(fetchRecentHistory());

      // 対応モデルの場合はストリーミングを有効にする（生成モードは結果をSSEのカードで返すため常にストリーミング）
      const useStreaming = Boolean(generationMode) || supportsChatStreaming(chatState.selectedAgent?.model_type);

      // 非同期APIでメッセージを送信
      console.log('🔍 Sending message with search enabled:', enableSearch);
      console.log('📎 Sending with uploaded_files:', userMessage.uploaded_files);
      console.log('📦 Sending with product_id:', productId);
      console.log('🔄 Sending with enable_streaming:', useStreaming);
      console.log('🎨 Sending with generation_mode:', generationMode);
      const response = await chatService.sendMessage({
        chat_id: currentChatId,
        content: messageContent,
        has_attachments: (attachments && attachments.length > 0) || (userMessage.uploaded_files && userMessage.uploaded_files.length > 0),
        uploaded_files: userMessage.uploaded_files,
        enable_search: enableSearch,
        product_id: productId,
        enable_streaming: useStreaming,
        generation_mode: generationMode,
        product_focus: productFocus
      });
      
      console.log('✅ handleSendMessageAsync: 非同期メッセージ送信成功', response);
      
      // AIメッセージのプレースホルダーを追加
      const aiMessagePlaceholder: Message = {
        id: response.ai_message_id,
        content: '',
        sender_type: 'ai',
        created_at: new Date().toISOString(),
        status: 'processing'
      };
      chatState.addMessage(aiMessagePlaceholder);
      
      // ThinkingProcessを表示
      if (chatState.setShowThinkingProcess && chatState.setThinkingMessage) {
        chatState.setShowThinkingProcess(true);
        chatState.setThinkingMessage(messageContent);
      }

      // 対応モデルの場合はストリーミング、それ以外はポーリング
      if (useStreaming) {
        // AIストリーミング接続を開始
        // Ver23: Webpack HMR test
        console.log('🔄 Starting AI streaming for message:', response.ai_message_id);

        // トークンを取得（tokenManager から）
        const token = tokenManager.getToken() || '';
        console.log('🔑 Token for streaming:', {
          hasToken: !!token,
          tokenLength: token?.length || 0,
          tokenPreview: token ? token.substring(0, 10) + '...' : 'NO TOKEN'
        });

        streamingService.startStreaming({
          chatId: currentChatId,
          messageId: response.ai_message_id,
          token,
          enableSearch,
          productId,
          onDelta: (delta: string, fullResponse: string) => {
            // デバッグログ - 受信したデータを即座に表示
            console.log('📝 [既存チャット] onDelta received:', {
              delta_length: delta?.length || 0,
              delta_preview: delta?.substring(0, 50),
              fullResponse_length: fullResponse?.length || 0,
              fullResponse_preview: fullResponse?.substring(0, 100)
            });

            // リアルタイムでメッセージを更新
            chatState.setMessages((prevMessages: Message[]) => {
              return prevMessages.map((msg) =>
                msg.id === response.ai_message_id
                  ? { ...msg, content: fullResponse, status: 'processing' as const }
                  : msg
              );
            });
          },
          onComplete: (messageId: number, content: string) => {
            console.log('✅ [既存チャット] Streaming completed:', messageId);

            // AI応答完了
            chatState.setTypingIndicator(false);
            chatState.setShowThinkingProcess(false);
            chatState.setThinkingMessage('');
            // AIのthinking内容をクリア
            if (chatState.setAiThinkingContent) {
              chatState.setAiThinkingContent('');
            }

            // メッセージを最終更新
            const aiMessage: Message = {
              id: messageId,
              content,
              sender_type: 'ai',
              created_at: new Date().toISOString(),
              status: 'completed'
            };

            chatState.setMessages((prevMessages: Message[]) => {
              return prevMessages.map((msg) =>
                msg.id === messageId ? { ...msg, ...aiMessage } : msg
              );
            });

            if (onPollingComplete) {
              onPollingComplete(aiMessage);
            }

            // サジェストを非同期ポーリングで取得
            pollForSuggestions(messageId);
          },
          onError: (error: string) => {
            console.error('❌ Streaming error:', error);

            chatState.setTypingIndicator(false);
            chatState.setShowThinkingProcess(false);
            chatState.setThinkingMessage('');
            // AIのthinking内容をクリア
            if (chatState.setAiThinkingContent) {
              chatState.setAiThinkingContent('');
            }

            if (onPollingError) {
              onPollingError(error);
            } else {
              showToast(error, 'error');
            }
          },
          onSearchProgress: (message: string) => {
            console.log('🔍 Search progress:', message);
          },
          onSearchResult: (result) => {
            console.log('📄 Search result:', result);
          },
          onSearchError: (error: string) => {
            console.error('❌ Search error:', error);
          },
          onThinking: (delta: string, fullThinking: string) => {
            console.log('💭 [handleSendMessageAsync] onThinking received:', {
              delta_length: delta?.length || 0,
              fullThinking_length: fullThinking?.length || 0
            });
            if (chatState.setAiThinkingContent) {
              chatState.setAiThinkingContent(fullThinking);
            }
          },
          onToolEvent: (event: ToolStreamEvent) => {
            chatState.setMessages((prevMessages: Message[]) => {
              return prevMessages.map((msg) =>
                msg.id === response.ai_message_id
                  ? { ...msg, tool_calls: mergeToolEvent(msg.tool_calls, event) }
                  : msg
              );
            });
          }
        });
      } else {
        // 従来のポーリング方式
        const pollForAIResponse = async () => {
          const maxAttempts = 60;
          let attempts = 0;
          let delay = 2000;

          const checkStatus = async (): Promise<void> => {
            try {
              const status = await chatService.getMessageStatus(response.ai_message_id);

              if (status.status === 'completed') {
                // AI応答が完了
                chatState.setTypingIndicator(false);
                chatState.setShowThinkingProcess(false);
                chatState.setThinkingMessage('');

                // メッセージを更新
                const aiMessage: Message = {
                  id: status.message_id,
                  content: status.content || '',
                  sender_type: 'ai',
                  created_at: status.created_at,
                  suggestions: status.suggestions || [],
                  status: 'completed'
                };

                // プレースホルダーを実際のメッセージに置き換え
                // 既存のメッセージを更新
                chatState.addMessage(aiMessage);

                // サジェストを設定
                if (status.suggestions && status.suggestions.length > 0 && chatState.setSuggestions) {
                  chatState.setSuggestions(status.suggestions);
                }

                if (onPollingComplete) {
                  onPollingComplete(aiMessage);
                }

                return; // ポーリング終了
              } else if (status.status === 'failed') {
                throw new Error(status.error_message || 'メッセージ処理に失敗しました');
              }

              // まだ処理中の場合、続行
              attempts++;
              if (attempts < maxAttempts) {
                delay = Math.min(delay * 1.2, 10000);
                setTimeout(checkStatus, delay);
              } else {
                throw new Error('タイムアウト: AI応答の取得に時間がかかっています');
              }
            } catch (error: any) {
              console.error('ポーリングエラー:', error);
              chatState.setTypingIndicator(false);
              chatState.setShowThinkingProcess(false);
              chatState.setThinkingMessage('');

              if (onPollingError) {
                onPollingError(error.message || 'AI応答の取得に失敗しました');
              }
            }
          };

          // 初回チェック開始
          setTimeout(checkStatus, 2000);
        };

        // ポーリングを開始
        pollForAIResponse();
      }
      
      // ローディング状態を解除
      chatState.setIsLoading(false);
      
    } catch (error) {
      console.error('handleSendMessageAsync: メッセージ送信に失敗:', error);
      showToast('メッセージの送信に失敗しました。もう一度お試しください。', 'error');
      chatState.setTypingIndicator(false);
      chatState.setIsLoading(false);
      
      if (onPollingError) {
        onPollingError(error.message || 'メッセージ送信に失敗しました');
      }
    }
  };

  /**
   * メッセージを送信する処理（添付ファイル付き - 同期版）
   */
  const handleSendMessage = async (attachments?: File[], uploadedFiles?: any[], enableSearch: boolean = false, productId?: number) => {
    console.log('🎯 useChatActions handleSendMessage called:', {
      messageInput: chatState.messageInput,
      attachments_count: attachments?.length || 0,
      uploadedFiles_count: uploadedFiles?.length || 0,
      uploadedFiles_data: uploadedFiles,
      selectedAgent: chatState.selectedAgent?.id,
      chatId: chatState.chatId,
      isNewChat: chatState.isNewChat,
      productId
    });

    // メッセージまたは添付ファイル、アップロード済みファイル、商品選択がない場合は送信しない
    if (!chatState.messageInput.trim() && (!attachments || attachments.length === 0) && (!uploadedFiles || uploadedFiles.length === 0) && !productId) return;

    // クレジット制限チェック
    if (user) {
      // 組織に所属している場合は組織のクレジット制限を確認
      if (user.organization_id && chatState.orgTokenInfo) {
        if (chatState.orgTokenInfo.is_limit_reached) {
          showToast('組織のクレジット上限に達しました。管理者にお問い合わせください。', 'error');
          return;
        }
      }
    }

    chatState.setIsLoading(true);
    
    // 送信するメッセージの内容を保存
    let messageContent = chatState.messageInput;
    // 入力欄をクリア
    chatState.setMessageInput('');
    
    try {
      if (chatState.isNewChat) {
        let agentToUse = chatState.selectedAgent;
        if (!agentToUse) { // 新規チャットでエージェントが明示的に選択されていない場合
          const defaultAgent = chatState.agents.find(agent => agent.id === 1);
          if (defaultAgent) {
            agentToUse = defaultAgent;
            // UI上のselectedAgentは更新しない（カードリストは表示されたままにするため）
            // ただし、送信後はそのエージェントが選択されたことになるので、
            // handleCreateNewChat内でnavigate後にselectedAgentを更新するなどの工夫が必要かもしれない
          } else {
            showToast('デフォルトのエージェント(ID:1)が見つかりませんでした。リストからエージェントを選択してください。', 'error');
            chatState.setIsLoading(false);
            return;
          }
        }
        await handleCreateNewChat(messageContent, agentToUse, attachments, uploadedFiles, enableSearch, productId);
        return;
      }
      
      // 既存のチャットIDを数値変換
      if (!chatState.chatId || isNaN(Number(chatState.chatId))) {
        // 既存チャットIDが無効な場合は、新規チャットとして扱う（フォールバック）
        let agentToUseForFallback = chatState.selectedAgent;
        if(!agentToUseForFallback) {
            const defaultAgent = chatState.agents.find(agent => agent.id === 1);
            if(defaultAgent) agentToUseForFallback = defaultAgent;
            else {
                showToast('送信先エージェントが見つかりません。', 'error');
                chatState.setIsLoading(false);
                return;
            }
        }
        await handleCreateNewChat(messageContent, agentToUseForFallback, attachments, uploadedFiles, enableSearch, productId);
        return;
      }

      const currentChatId = Number(chatState.chatId);

      // ユーザーメッセージをUIに追加（すぐに表示 - ユーザーメッセージは常に即時表示）
      const userMessage: Message = {
        id: chatState.messages.length + 1,
        content: messageContent,
        sender_type: 'user',
        created_at: new Date().toISOString(),
        uploaded_files: uploadedFiles || undefined
      };
      
      // 添付ファイルがある場合、処理を分岐
      if (attachments && attachments.length > 0) {
        try {
          console.log('handleSendMessage: 添付ファイルを処理します:', attachments.length, '個');
          
          // ドキュメントファイルをチェック
          const documentFiles = attachments.filter(file => 
            fileUploadService.isSupportedDocumentFile(file)
          );
          
          // サポートされていないファイルをチェック（すべてのメディアファイルはdocumentFilesに含まれる）
          const mediaFiles = attachments.filter(file => 
            !fileUploadService.isSupportedDocumentFile(file)
          );
          
          console.log('📁 ファイル分類結果:', {
            total_files: attachments.length,
            document_files: documentFiles.map(f => ({ name: f.name, type: f.type })),
            media_files: mediaFiles.map(f => ({ name: f.name, type: f.type })),
            is_image: attachments.map(f => ({ name: f.name, isImage: fileUploadService.isImageFile(f) }))
          });
          
          // ドキュメントファイルがある場合はAIサービスに直接アップロード（Excel、PowerPoint、画像、動画、PDF等）
          if (documentFiles.length > 0) {
            console.log('handleSendMessage: ドキュメントファイルをAIサービスにアップロードします:', documentFiles.length, '個');
            
            for (const file of documentFiles) {
              const isExcel = fileUploadService.isExcelFile(file);
              const isPowerPoint = fileUploadService.isPowerPointFile(file);
              const isImage = fileUploadService.isImageFile(file);
              
              console.log('🔍 handleSendMessage: ファイル判定結果', {
                file_name: file.name,
                file_type: file.type,
                isExcel,
                isPowerPoint,
                isImage,
                extension: file.name.split('.').pop()?.toLowerCase()
              });
              
              if (isExcel || isPowerPoint || isImage) {
                // Excel/PowerPoint/画像はAIサービスに直接アップロード
                // o3やo1などのOpenAIモデルは'openai'にマッピング
                let aiModel = chatState.selectedAgent?.model_type || 'openai';
                if (aiModel.startsWith('o1') || aiModel.startsWith('o3') || aiModel.startsWith('gpt')) {
                  aiModel = 'openai';
                } else if (aiModel.startsWith('claude')) {
                  aiModel = 'claude';
                } else if (aiModel.startsWith('gemini')) {
                  aiModel = 'gemini';
                }
                console.log('handleSendMessage: AIサービスにファイルをアップロード中...', file.name, 'モデル:', aiModel);
                
                try {
                  let uploadedFile;
                  if (file.size > 10 * 1024 * 1024) {
                    // 10MB以上はチャンクアップロード
                    uploadedFile = await uploadLargeFile(
                      file,
                      aiModel,
                      currentChatId,
                      (progress) => {
                        console.log('アップロード進捗:', progress, '%');
                      }
                    );
                  } else {
                    // 10MB以下は通常のアップロード
                    uploadedFile = await uploadFile(file, aiModel, currentChatId);
                  }
                  
                  // 画像ファイルの場合、S3にもアップロード
                  if (isImage) {
                    console.log('🖼️ 画像をS3にもアップロード:', file.name);
                    const s3Result = await s3UploadService.uploadImageToS3(file);
                    if (s3Result) {
                      console.log('✅ S3アップロード成功:', s3Result.s3_url);
                      // S3のURLを保存（将来的に使用）
                      if (uploadedFile) {
                        uploadedFile.s3_url = s3Result.s3_url;
                        uploadedFile.s3_key = s3Result.s3_key;
                      }
                    }
                  }
                  
                  // アップロードしたファイル情報をメッセージに追加
                  console.log('🔍 handleSendMessage: Checking uploaded file response', {
                    uploadedFile,
                    uploadedFile_keys: uploadedFile ? Object.keys(uploadedFile) : null,
                    has_id: uploadedFile?.id ? 'YES' : 'NO',
                    has_file_id: uploadedFile?.file_id ? 'YES' : 'NO',
                    file_name: file.name
                  });
                  
                  if (uploadedFile && uploadedFile.file_id) {
                    messageContent += `\n\n[アップロード済みファイル: ${file.name} (ID: ${uploadedFile.file_id})]`;
                    userMessage.content = messageContent;
                    
                    // アップロード済みファイル情報を保存（後でsendMessageに渡すため）
                    if (!userMessage.uploaded_files) {
                      userMessage.uploaded_files = [];
                    }
                    userMessage.uploaded_files.push({
                      file_id: uploadedFile.file_id,
                      service: aiModel,
                      name: file.name,
                      mime_type: file.type,
                      s3_url: uploadedFile.s3_url,
                      s3_key: uploadedFile.s3_key,
                      s3_bucket: uploadedFile.s3_bucket
                    });

                    console.log('🔗 handleSendMessage: Added uploaded file to userMessage', {
                      file_name: file.name,
                      file_id: uploadedFile.file_id,
                      service: aiModel,
                      s3_url: uploadedFile.s3_url,
                      uploaded_files_total: userMessage.uploaded_files.length
                    });
                  } else {
                    console.error('❌ handleSendMessage: uploadedFile missing file_id', {
                      uploadedFile,
                      file_name: file.name
                    });
                  }
                } catch (error) {
                  console.error('💥 AIサービスへのファイルアップロードに失敗:', error);
                  console.error('💥 エラー詳細:', {
                    error_message: error.message,
                    error_response: error.response?.data,
                    error_status: error.response?.status,
                    file_name: file.name
                  });
                  // エラーでも通常の添付ファイルとして処理を続行
                  console.log('🔄 フォールバック: AttachmentControllerを使用します');
                  const result = await uploadAttachment(file, currentChatId, userMessage.id);
                  if (userMessage.attachments) {
                    userMessage.attachments.push(result);
                  } else {
                    userMessage.attachments = [result];
                  }
                }
              } else {
                // その他のドキュメントは通常の添付ファイルとして処理
                const result = await uploadAttachment(file, currentChatId, userMessage.id);
                if (userMessage.attachments) {
                  userMessage.attachments.push(result);
                } else {
                  userMessage.attachments = [result];
                }
              }
            }
            
            console.log('handleSendMessage: ドキュメントファイルのアップロード完了');
          }
          
          // 画像・動画ファイルがある場合
          if (mediaFiles.length > 0) {
            console.log('handleSendMessage: 画像・動画ファイルをアップロードします');
            const uploadedAttachments: Attachment[] = [];
            for (const file of mediaFiles) {
              const result = await uploadAttachment(file, currentChatId, userMessage.id);
              uploadedAttachments.push(result);
            }
            userMessage.attachments = uploadedAttachments;
          }
        } catch (error) {
          console.error('handleSendMessage: ファイル処理に失敗しました:', error);
          showToast('ファイルの処理に失敗しました。メッセージのみ送信します。', 'error');
          // エラーでもメッセージは送信続行
        }
      }
      
      // 応答待ちタイピングインジケーターを表示する
      chatState.setTypingIndicator(true);
      
      // ThinkingProcessを表示（機能フラグで制御）
      if (chatState.setShowThinkingProcess && chatState.setThinkingMessage) {
        chatState.setShowThinkingProcess(true);
        chatState.setThinkingMessage(messageContent);
      }
      
      // ユーザーメッセージはアニメーションなしで即時追加
      chatState.addMessage(userMessage);

      // メッセージ送信直後にサイドバーの履歴を更新（履歴の順番更新）
      dispatch(fetchRecentHistory());

      // 対応モデルの場合はストリーミングを有効にする
      const useStreaming = supportsChatStreaming(chatState.selectedAgent?.model_type);

      // 送信前にデバッグ情報を出力
      console.log('🚀 handleSendMessage: About to send message', {
        chat_id: currentChatId,
        content_length: messageContent.length,
        has_attachments: attachments && attachments.length > 0,
        userMessage_uploaded_files: userMessage.uploaded_files,
        userMessage_uploaded_files_count: userMessage.uploaded_files?.length || 0,
        product_id: productId,
        enable_streaming: useStreaming
      });

      // 非同期APIでメッセージを送信（添付ファイルとアップロード済みファイル情報を含める）
      const response = await chatService.sendMessage({
        chat_id: currentChatId,
        content: messageContent,
        has_attachments: attachments && attachments.length > 0,
        uploaded_files: userMessage.uploaded_files,
        enable_search: enableSearch,
        product_id: productId,
        enable_streaming: useStreaming
      });

      // AIメッセージのプレースホルダーを追加
      const aiMessagePlaceholder: Message = {
        id: response.ai_message_id,
        content: '',
        sender_type: 'ai',
        created_at: new Date().toISOString(),
        status: 'processing'
      };
      chatState.addMessage(aiMessagePlaceholder);

      // 対応モデルの場合はストリーミング、それ以外はポーリング
      if (useStreaming) {
        console.log('🔄 [handleSendMessage] Starting AI streaming for message:', response.ai_message_id);
        const token = tokenManager.getToken() || '';
        streamingService.startStreaming({
          chatId: currentChatId,
          messageId: response.ai_message_id,
          token,
          enableSearch,
          productId,
          onDelta: (delta: string, fullResponse: string) => {
            // デバッグログ - 受信したデータを即座に表示
            console.log('📝 [handleSendMessage] onDelta received:', {
              delta_length: delta?.length || 0,
              delta_preview: delta?.substring(0, 50),
              fullResponse_length: fullResponse?.length || 0,
              fullResponse_preview: fullResponse?.substring(0, 100)
            });

            chatState.setMessages((prevMessages: Message[]) => {
              return prevMessages.map((msg) =>
                msg.id === response.ai_message_id
                  ? { ...msg, content: fullResponse, status: 'processing' as const }
                  : msg
              );
            });
          },
          onComplete: (messageId: number, content: string) => {
            console.log('✅ [handleSendMessage] Streaming completed:', messageId);
            chatState.setTypingIndicator(false);
            if (chatState.showThinkingProcess) {
              chatState.setShowThinkingProcess(false);
              chatState.setThinkingMessage('');
            }
            // AIのthinking内容をクリア
            if (chatState.setAiThinkingContent) {
              chatState.setAiThinkingContent('');
            }
            const aiMessage: Message = {
              id: messageId,
              content,
              sender_type: 'ai',
              created_at: new Date().toISOString(),
              status: 'completed'
            };
            chatState.setMessages((prevMessages: Message[]) => {
              return prevMessages.map((msg) =>
                msg.id === messageId ? { ...msg, ...aiMessage } : msg
              );
            });
            chatState.setIsLoading(false);

            // サジェストを非同期ポーリングで取得
            pollForSuggestions(messageId);
          },
          onError: (error: string) => {
            console.error('❌ [handleSendMessage] Streaming error:', error);
            if (chatState.showThinkingProcess) {
              chatState.setShowThinkingProcess(false);
              chatState.setThinkingMessage('');
            }
            // AIのthinking内容をクリア
            if (chatState.setAiThinkingContent) {
              chatState.setAiThinkingContent('');
            }
            chatState.setTypingIndicator(false);
            chatState.setIsLoading(false);
          },
          onSearchProgress: (message: string) => console.log('🔍 Search progress:', message),
          onSearchResult: (result) => console.log('📄 Search result:', result),
          onSearchError: (error: string) => console.error('❌ Search error:', error),
          onThinking: (delta: string, fullThinking: string) => {
            console.log('💭 [handleSendMessage] onThinking received:', {
              delta_length: delta?.length || 0,
              fullThinking_length: fullThinking?.length || 0
            });
            if (chatState.setAiThinkingContent) {
              chatState.setAiThinkingContent(fullThinking);
            }
          },
          onToolEvent: (event: ToolStreamEvent) => {
            chatState.setMessages((prevMessages: Message[]) => {
              return prevMessages.map((msg) =>
                msg.id === response.ai_message_id
                  ? { ...msg, tool_calls: mergeToolEvent(msg.tool_calls, event) }
                  : msg
              );
            });
          }
        });
        return; // ストリーミングを使用する場合、ポーリングは実行しない
      }

      // ポーリングでAI応答を待つ（ストリーミング非対応モデル用）
      const pollForResponse = async () => {
        try {
          const status = await chatService.getMessageStatus(response.ai_message_id);

          if (status.status === 'completed') {
            console.log('新しいAI応答を受信:', status.content?.substring(0, 30) + '...');

            const aiResponse: Message = {
              id: response.ai_message_id,
              content: status.content || '',
              sender_type: 'ai',
              created_at: status.created_at,
              suggestions: status.suggestions,
              status: 'completed'
            };

            // サジェストを確認
            if (status.suggestions && Array.isArray(status.suggestions)) {
              console.log('AIから返答サジェストを受信:', status.suggestions.length, '個');
              
              if (chatState.setSuggestions && typeof chatState.setSuggestions === 'function') {
                chatState.setSuggestions(status.suggestions);
              }
            }
            
            // ThinkingProcessを閉じてからメッセージを更新
            if (chatState.showThinkingProcess) {
              chatState.setShowThinkingProcess(false);
              chatState.setThinkingMessage('');
            }
            
            // プレースホルダーを実際のメッセージに置き換え（暫定的にaddMessageで対応）
            chatState.addMessage(aiResponse);
            
            // チャット情報を更新
            try {
              const chatResponse = await chatService.getChatById(currentChatId);
              if (chatResponse) {
                chatState.setChats(prevChats =>
                  prevChats.map(chat =>
                    chat.id === chatResponse.id
                      ? { ...chat, title: chatResponse.title || chat.title }
                      : chat
                  )
                );
              }
            } catch (error) {
              console.error('チャット情報の取得に失敗:', error);
            } finally {
              // サイドバーの履歴を再取得してタイトルを更新（成功・失敗問わず）
              dispatch(fetchRecentHistory());
            }

            // ロード状態とタイピングインジケーターを解除
            chatState.setTypingIndicator(false);
            chatState.setIsLoading(false);
          } else if (status.status === 'failed') {
            showToast('AI応答の処理に失敗しました。', 'error');
            chatState.setTypingIndicator(false);
            chatState.setIsLoading(false);
            if (chatState.showThinkingProcess) {
              chatState.setShowThinkingProcess(false);
              chatState.setThinkingMessage('');
            }
          } else {
            // まだ処理中の場合、2秒後に再確認
            setTimeout(pollForResponse, 2000);
          }
        } catch (error) {
          console.error('AI応答の取得に失敗:', error);
          showToast('AI応答の取得に失敗しました。', 'error');
          chatState.setTypingIndicator(false);
          chatState.setIsLoading(false);
          if (chatState.showThinkingProcess) {
            chatState.setShowThinkingProcess(false);
            chatState.setThinkingMessage('');
          }
        }
      };

      // 2秒後にポーリング開始
      setTimeout(pollForResponse, 2000);
    } catch (error) {
      showToast('メッセージの送信に失敗しました。もう一度お試しください。', 'error');
      // エラー時はすぐにローディングを終了
      chatState.setTypingIndicator(false);
      chatState.setIsLoading(false);

      // エラー時もThinkingProcessを閉じる
      if (chatState.showThinkingProcess) {
        chatState.setShowThinkingProcess(false);
        chatState.setThinkingMessage('');
      }
    }
  };

  /**
   * エージェントを変更する処理
   */
  const handleChangeAgent = (agentId: number, agents: AgentWithIcon[], setSelectedAgent: (agent: AgentWithIcon) => void, setAgentSelectorVisible: (visible: boolean) => void) => {
    const agent = agents.find(a => a.id === agentId);
    if (agent) {
      setSelectedAgent(agent);
    }
    setAgentSelectorVisible(false);
  };

  /**
   * ログアウトする処理
   */
  const handleLogout = async () => {
    console.log('=== handleLogout関数が呼び出されました ===');
    console.log('dispatch関数の存在:', !!dispatch);
    console.log('logout関数の存在:', !!logout);
    
    try {
      console.log('Redux logoutアクションをdispatchします...');
      const result = await dispatch(logout()).unwrap();
      console.log('Redux logoutアクション完了:', result);
      console.log('ログアウト成功、ログインページへリダイレクト');
      // ログインページへリダイレクト
      window.location.href = '/login';
    } catch (error) {
      console.error('ログアウトエラー - 詳細:', error);
      console.error('エラーの型:', typeof error);
      console.error('エラースタック:', error?.stack);
      // エラーがあってもログインページへリダイレクト
      console.log('エラーがありましたが、ログインページへリダイレクトします');
      window.location.href = '/login';
    }
  };

  /**
   * ツールカードのボタン（確認・再実行）用: 入力欄の state を経由せず本文を直接送る。
   */
  const sendTextMessage = async (text: string): Promise<void> => {
    await handleSendMessageAsync(undefined, undefined, false, undefined, undefined, undefined, undefined, undefined, text);
  };

  return {
    handleSendMessage,
    handleSendMessageAsync,
    handleSendDirectMessage,
    handleCreateNewChat,
    handleChangeAgent,
    handleLogout,
    sendTextMessage
  };
};
